import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewEncapsulation, inject } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, switchMap, tap } from 'rxjs';

// A customer search box: matches as you type (name, email or phone, several
// words in any order), highlights what matched, and shows enough about each
// person - type, orders, points - to pick the right one.
@Component({
    selector: 'app-customer-typehead',
    imports: [MatAutocompleteModule, MatIconModule, MatProgressSpinnerModule, FormsModule, ReactiveFormsModule, DatePipe, DecimalPipe],
    templateUrl: './customer-typehead.component.html',
    styleUrl: './customer-typehead.component.css',
    encapsulation: ViewEncapsulation.None
})
export class CustomerTypeheadComponent implements OnInit {
  @Input() dataSet: any[] = [];
  @Input() prevData?: any;
  @Input() resetEachSelect?: boolean = false;
  // When given, matches come from the server as the user types (debounced)
  // rather than from an in-memory dataSet.
  @Input() searchFn?: (keyword: string) => Observable<any[]>;
  @Input() maxResults = 20; // what the server returns at most - tells us when there may be more
  @Output() onModelChangedEvent = new EventEmitter<any>();

  myControl = new FormControl<string | any>('');
  options: any[] = this.dataSet;

  items: any[] = [];
  query = '';
  loading = false;
  private selected: any = null;
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.options = this.dataSet;

    this.myControl.valueChanges
      .pipe(
        tap((value) => {
          // Typing over a chosen customer un-chooses them.
          if (typeof value === 'string' && this.selected) {
            this.selected = null;
            this.onModelChangedEvent.emit(null);
          }
        }),
        map((value) => (typeof value === 'string' ? value.trim() : '')),
        tap((keyword) => {
          this.query = keyword;
          if (keyword.length < 2) {
            this.items = [];
            this.loading = false;
          } else if (this.searchFn) {
            this.loading = true;
          }
        }),
        debounceTime(this.searchFn ? 250 : 0),
        distinctUntilChanged(),
        switchMap((keyword) => {
          if (keyword.length < 2) return of([]);
          return this.searchFn ? this.searchFn(keyword).pipe(catchError(() => of([]))) : of(this._filter(keyword));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.items = results;
        this.loading = false;
      });

    if (this.prevData) {
      this.myControl.setValue(this.prevData);
    }
  }

  displayFn(dataObj: any): string {
    return dataObj && dataObj.firstname ? `${dataObj.firstname} ${dataObj.lastname}` : '';
  }

  get hasText(): boolean {
    const v = this.myControl.value;
    return typeof v === 'string' ? v.length > 0 : !!v;
  }

  clear() {
    this.myControl.setValue('');
  }

  onSelected(option: any) {
    if (option?.userid) {
      this.selected = option;
      this.onModelChangedEvent.emit(option);
      if (this.resetEachSelect) {
        this.selected = null;
        this.myControl.setValue('');
      }
    }
  }

  // ---- display helpers ----
  initials(u: any): string {
    const a = (u?.firstname || u?.useremail || '?')[0];
    const b = (u?.lastname || '')[0] || '';
    return (a + b).toUpperCase();
  }

  private static readonly AVATARS = [
    'bg-amber-100 text-amber-800', 'bg-sky-100 text-sky-800', 'bg-emerald-100 text-emerald-800',
    'bg-violet-100 text-violet-800', 'bg-rose-100 text-rose-800', 'bg-orange-100 text-orange-800',
  ];

  avatarClass(u: any): string {
    return CustomerTypeheadComponent.AVATARS[Number(u?.userid || 0) % CustomerTypeheadComponent.AVATARS.length];
  }

  // Trade accounts stand out; otherwise the customer level, when there is one.
  kind(u: any): { label: string; cls: string } | null {
    const roles = String(u?.userrole ?? '');
    if (roles.includes('TRADER')) return { label: 'Trade', cls: 'bg-neutral-900 text-amber-300' };
    const type = String(u?.usertype ?? '').toUpperCase();
    if (type && type !== 'NONMEMBER') return { label: type.charAt(0) + type.slice(1).toLowerCase(), cls: 'bg-amber-100 text-amber-800' };
    return null;
  }

  // Splits text into plain / matched pieces for highlighting.
  segments(text: string | undefined, query: string): { text: string; match: boolean }[] {
    const value = String(text ?? '');
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return [{ text: value, match: false }];
    const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`(${escaped.join('|')})`, 'ig');
    return value
      .split(re)
      .filter((part) => part !== '')
      .map((part) => ({ text: part, match: terms.includes(part.toLowerCase()) }));
  }

  private _filter(text: string): any[] {
    const terms = text.toLowerCase().split(/\s+/).filter(Boolean);
    return this.options
      .filter((o) => {
        const hay = [o.displayname, o.firstname, o.lastname, o.phone, o.useremail].map((v) => String(v ?? '').toLowerCase()).join(' ');
        return terms.every((t) => hay.includes(t));
      })
      .slice(0, this.maxResults);
  }
}
